import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/shared/api/client';

import styles from './ui.module.css';

const translatedCodes = new Set([
  'invalid_argument',
  'unauthenticated',
  'permission_denied',
  'already_exists',
  'too_many_requests',
  'not_found',
  'unavailable',
  'timeout',
  'internal',
]);

/**
 * Human-readable text for a failed request.
 *
 * The server message is never shown: it is written for developers, in English,
 * and may change. The text comes from the error code, and from the field and
 * reason when the server names the field to fix. `fetch` reports a lost
 * connection as a TypeError.
 */
export function describeError(error: unknown, t: TFunction): string {
  if (error instanceof TypeError) {
    return t('errors.network');
  }

  if (!(error instanceof ApiError)) {
    return t('errors.generic');
  }

  const fallback = translatedCodes.has(error.code)
    ? t(`errors.${error.code}`)
    : t('errors.generic');

  if (error.field !== undefined && error.reason !== undefined) {
    return t(`validation.${error.field}.${error.reason}`, {
      defaultValue: t(`validation.any.${error.reason}`, { defaultValue: fallback }),
    });
  }

  return fallback;
}

function isServerFailure(error: unknown): error is ApiError {
  return error instanceof ApiError && (error.status >= 500 || error.code === 'unknown');
}

/** Shows a failed request together with the id to quote in a bug report. */
export function ErrorMessage({ error }: { error: unknown }): React.JSX.Element {
  const { t } = useTranslation();
  const requestId = isServerFailure(error) ? error.requestId : undefined;

  return (
    <p className={styles.error} role="alert">
      {describeError(error, t)}
      {requestId !== undefined && (
        <span className={styles.hint}> ({t('common.requestId', { id: requestId })})</span>
      )}
    </p>
  );
}
